import { GET_VIRTUAL_FARM_DETAILS_REQUEST } from "./types.js";
import {
  getVirtualFarmDetails,
  getVirtualFarmDetailsError,
  getVirtualFarmDetailsSuccess,
} from "./actions.js";
import { put, take, call } from "redux-saga/effects";
import { getServerResponseStatus } from "../ErrorPopUp/actions";
import {
  startLoading,
  endLoading,
} from "../../common/redux/loadManager/actions";
import { getLoadId } from "../../common/redux/loadManager/methods";
import {
  cowCoordinates,
  decorateCowsWithBreed,
  decorateVirtualFarmDataWithPositionInformation,
  getBreedPercentageDictionaryByBreedProportionConfig,
} from "./util.js";
import { MOCK_BREED_DATA } from "../../api/mockApi/virtualFarmApi.js";
import { MOCK_SUSTAINABILITY_INFO_API_SUCCESSFUL_RESPONSE } from "../../api/mockApi/sustainabilityInfoApi.js";
import { redirect } from "../CustomRedirect/actions.js";
import {
  initializeLanguageWithBackendSettings,
  setDefaultLanguage,
  setSupportedLanguagesForFarm,
} from "../../localization/TranslationUtil.js";

let currentLoadId = 0;

export default function* virtualFarmDetailsSaga(api) {
  while (true) {
    const farmDetailsRequestAction = yield take(
      GET_VIRTUAL_FARM_DETAILS_REQUEST,
    );
    /** Update state when server is online */
    yield put(getServerResponseStatus(""));
    /** Run loader */
    currentLoadId = getLoadId();
    yield put(startLoading(currentLoadId));
    const { farmId, batchId } = farmDetailsRequestAction.payload;
    yield call(getFarmDetails, api, farmId, batchId);
  }
}

/** Create request to get virtual farm details
 * @param {string} accessToken - User access token
 * @param {any} farmId - Farm id
 */
function* getFarmDetails(api, farmId, batchId) {
  let batchless = false;
  try {
    let virtualFarmDetails;

    if (!batchId) {
      // No batchId in URL → treat like farm-only
      batchless = true;
      virtualFarmDetails = yield call(
        [api, api.getVirtualFarmDetails],
        farmId,
        null,
      );
    } else {
      try {
        virtualFarmDetails = yield call(
          [api, api.getVirtualFarmDetails],
          farmId,
          batchId,
        );
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404) {
          batchless = true;
          virtualFarmDetails = yield call(
            [api, api.getVirtualFarmDetails],
            farmId,
            null,
          );
        } else {
          throw error;
        }
      }
    }

    const batch = virtualFarmDetails.data.milkBatch;
    const cowsFromMilkBatch = batch
      ? batch.cows
      : virtualFarmDetails.data.cowsWithFullInfo;

    const displayedCowsFromMilkBatch = cowsFromMilkBatch.slice(
      0,
      cowCoordinates.length,
    );
    const restOfCowsFromMilkBatch = cowsFromMilkBatch.slice(
      cowCoordinates.length,
    );

    const externalIdsOfCowsFromMilkBatch = cowsFromMilkBatch.map(
      (cow) => cow.externalId,
    );
    const cowsOutsideOfMilkBatch = virtualFarmDetails.data.allCows.filter(
      (cow) => !externalIdsOfCowsFromMilkBatch.includes(cow.externalId),
    );

    decorateCowsWithBreed(
      displayedCowsFromMilkBatch,
      virtualFarmDetails?.data?.breeds,
    );
    decorateCowsWithBreed(
      restOfCowsFromMilkBatch,
      virtualFarmDetails?.data?.breeds,
    );
    decorateCowsWithBreed(
      cowsOutsideOfMilkBatch,
      virtualFarmDetails?.data?.breeds,
    );

    const decoratedData = decorateVirtualFarmDataWithPositionInformation(
      virtualFarmDetails?.data,
    );
    decoratedData.batchless = batchless;
    if (virtualFarmDetails?.data?.farmer?.languages?.length > 0) {
      setSupportedLanguagesForFarm(virtualFarmDetails?.data?.farmer?.languages);
      setDefaultLanguage(virtualFarmDetails?.data?.farmer?.languages);
      initializeLanguageWithBackendSettings();
    }
    yield put(getVirtualFarmDetailsSuccess(decoratedData));
    yield put(endLoading(currentLoadId));
  } catch (error) {
    console.log("@final error:", error);
    yield put(getVirtualFarmDetailsError(error));
    yield put(getServerResponseStatus(error));
    yield put(endLoading(currentLoadId));
    yield put(redirect("notFound"));
  }
}
